import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Drawer,
  SwipeableDrawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
  Box,
  Divider,
  useMediaQuery,
  Typography,
  TextField,
  InputAdornment,
  Avatar,
  Tooltip,
  alpha,
  Popover,
  Switch
} from '@mui/material';
// Phosphor Icons (react-icons/pi) - same library as hk-bedrock-chat
import {
  PiCaretLeft,
  PiCaretRight,
  PiCaretUp,
  PiCaretDown,
  PiSquaresFour,
  PiBriefcase,
  PiChatCenteredDots,
  PiGear,
  PiRobot,
  PiShield,
  PiFile,
  PiMagnifyingGlass,
  PiX,
  PiSliders,
  PiSignOut,
  PiEnvelope
} from 'react-icons/pi';
// Theme icons from io5 (same as hk-bedrock-chat)
import { IoMoonSharp, IoSunnyOutline } from 'react-icons/io5';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../services/apiService';
import { useRBAC } from '../contexts/RBACContext';
import { useTheme } from '@mui/material/styles';
import { useWorkflowSearch } from '../contexts/WorkflowSearchContext';
import { resolvePracticeIcon } from '../utils/practiceIconResolver';
import { useAuth } from '../auth/AuthProvider';

interface UnifiedSidebarProps {
  open: boolean;
  collapsed: boolean;
  onToggle: () => void;
  onCollapse: () => void;
  onClose?: () => void;
  variant?: 'permanent' | 'persistent' | 'temporary';
  themeMode: 'light' | 'dark' | 'retro';
  onToggleTheme: (ctrlKey?: boolean) => void;
}

const DRAWER_WIDTH = 280;
const COLLAPSED_WIDTH = 64;

export default function UnifiedSidebar({ 
  open, 
  collapsed,
  onToggle, 
  onCollapse,
  onClose, 
  variant = 'persistent',
  themeMode,
  onToggleTheme
}: UnifiedSidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { isAdmin, canAccessWorkflowStudio } = useRBAC();
  const [expandedPractices, setExpandedPractices] = useState<string[]>([]);
  const [expandedForms, setExpandedForms] = useState<boolean>(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const isTemporary = variant === 'temporary';
  const theme = useTheme();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('md'));
  const touchFriendly = isTemporary || isMobileViewport;
  const { searchTerm, setSearchTerm, clearSearch } = useWorkflowSearch();
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  // Style guide tokens
  const borderColor = theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.15)' 
    : 'rgba(1, 33, 105, 0.1)';
  
  const activeItemBg = theme.palette.mode === 'dark'
    ? alpha(theme.palette.primary.main, 0.15)
    : 'rgba(1, 33, 105, 0.08)';

  const hoverBg = theme.palette.mode === 'dark'
    ? alpha(theme.palette.common.white, 0.10)
    : theme.palette.grey[100];

  const drawerPaperSx = {
    width: collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH,
    boxSizing: 'border-box',
    top: 0,
    height: '100vh',
    borderRight: `1px solid ${borderColor}`,
    transition: 'width 0.3s ease',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  } as const;
  
  // HK Style Guide: Exact nav item styling from hk-bedrock-chat
  // padding: py-2.5 px-4 (10px 16px), margin: mx-2 my-0.5 (8px 2px)
  // font-size: 14px, font-weight: 500, gap: 16px, border-radius: 8px
  const baseButtonSx = {
    py: 1.25,           // 10px vertical padding
    px: 2,              // 16px horizontal padding
    mx: 1,              // 8px horizontal margin
    my: 0.25,           // 2px vertical margin
    minHeight: 40,
    borderRadius: 1,    // 8px
    fontSize: 14,       // text-sm
    fontWeight: 500,    // font-medium
    gap: 2,             // 16px gap between icon and text
    transition: 'all 0.15s ease',
    '&:hover': {
      backgroundColor: hoverBg,
      color: theme.palette.primary.main,
    },
    '&.Mui-selected': {
      backgroundColor: activeItemBg,
      color: theme.palette.primary.main,
      '&:hover': {
        backgroundColor: activeItemBg,
      },
    },
  } as const;
  
  const nestedLevelOneSx = { ...baseButtonSx, pl: collapsed ? 2 : 4 } as const;
  const nestedLevelTwoSx = { ...baseButtonSx, pl: collapsed ? 2 : 5 } as const;
  
  // HK Style Guide: Icon container 20x20px (h-5 w-5)
  const listItemIconSx = {
    minWidth: collapsed ? 0 : 36,
    width: 20,
    height: 20,
    justifyContent: 'center',
    color: 'inherit',
    flexShrink: 0,
  } as const;
  
  // HK Style Guide: Section header styling (text-xs font-semibold uppercase tracking-wider)
  const sectionHeaderSx = {
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: theme.palette.text.secondary,
    px: 2,
    py: 1,
    mt: 1.5,
  } as const;
  
  const closeDrawer = () => {
    if (onClose) {
      onClose();
    } else {
      onToggle();
    }
  };
  
  const handleSwipeOpen = () => {
    if (!open) {
      onToggle();
    }
  };
  
  const handleSwipeClose = () => {
    closeDrawer();
  };
  
  // Feature flags
  const aiChatEnabled = process.env.REACT_APP_AI_CHAT_ENABLED === 'true';
  const customFormsEnabled = process.env.REACT_APP_CUSTOM_FORMS_ENABLED === 'true';
  const formBuilderEnabled = process.env.REACT_APP_FORM_BUILDER_ENABLED === 'true';

  const { data: practices = [] } = useQuery({
    queryKey: ['practices-sidebar'],
    queryFn: () => apiService.getPracticeDetails('')
  });

  const { data: sidebarForms } = useQuery({
    queryKey: ['sidebar-forms'],
    queryFn: () => apiService.getSidebarForms?.() || Promise.resolve({ forms: [], total: 0 }),
    enabled: !!apiService.getSidebarForms,
    refetchInterval: 60000
  });

  // practice icon config from S3
  const { data: iconConfig } = useQuery({
    queryKey: ['practice-icons'],
    queryFn: () => apiService.get('/api/config/practice-icons'),
    staleTime: 5 * 60 * 1000,
  });

  const formatTitle = (str: string) => 
    str.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

  // Filter practices and workflows based on search term
  const filteredPractices = useMemo(() => {
    if (!searchTerm.trim()) return practices;
    
    const lowerSearch = searchTerm.toLowerCase().trim();
    
    return practices
      .map((practice: any) => {
        const practiceNameMatch = formatTitle(practice.name).toLowerCase().includes(lowerSearch) ||
                                  practice.id.toLowerCase().includes(lowerSearch);
        
        const matchingWorkflows = practice.workflows?.filter((workflow: any) => {
          const workflowName = workflow.ui?.displayName || formatTitle(workflow.name);
          return workflowName.toLowerCase().includes(lowerSearch) ||
                 workflow.id.toLowerCase().includes(lowerSearch) ||
                 (workflow.description && workflow.description.toLowerCase().includes(lowerSearch));
        }) || [];

        if (practiceNameMatch || matchingWorkflows.length > 0) {
          return {
            ...practice,
            workflows: practiceNameMatch ? practice.workflows : matchingWorkflows
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [practices, searchTerm]);

  // Auto-expand practices when searching
  useEffect(() => {
    if (searchTerm.trim()) {
      const practiceIds = filteredPractices.map((p: any) => p.id);
      setExpandedPractices(practiceIds);
    }
  }, [searchTerm, filteredPractices]);

  // Filter forms based on search term
  const filteredForms = useMemo(() => {
    if (!searchTerm.trim() || !sidebarForms?.forms) return sidebarForms?.forms || [];
    
    const lowerSearch = searchTerm.toLowerCase().trim();
    return sidebarForms.forms.filter((form: any) => 
      form.title.toLowerCase().includes(lowerSearch) ||
      form.id.toLowerCase().includes(lowerSearch)
    );
  }, [sidebarForms, searchTerm]);

  // Auto-expand forms when searching and there are matches
  useEffect(() => {
    if (searchTerm.trim() && filteredForms.length > 0) {
      setExpandedForms(true);
    }
  }, [searchTerm, filteredForms.length]);

  // HK Style Guide Practice Area Icons (PNG from /public/icons/)
  // Icons are inverted in dark mode via CSS filter
  // Container ensures proper sizing; image scales to fit while preserving aspect ratio
  const HKIcon = ({ src, alt }: { src: string; alt: string }) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 24,
        height: 24,
      }}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        sx={{
          maxHeight: '100%',
          maxWidth: '100%',
          objectFit: 'contain',
          // Invert to white in dark mode
          filter: theme.palette.mode === 'dark' ? 'brightness(0) invert(1)' : 'none',
        }}
      />
    </Box>
  );

  // Map practice IDs to HK iconography
  // Includes aliases for different ID formats (e.g., 'ip' and 'patent' both map to IP icon)
  const practiceIconMap: Record<string, string> = {
    // Intellectual Property (multiple possible IDs)
    'patent': '/icons/Intellectual Property.png',
    'ip': '/icons/Intellectual Property.png',
    'intellectual-property': '/icons/Intellectual Property.png',
    // Real Estate (with and without hyphen)
    'real-estate': '/icons/Real Estate.png',
    'realestate': '/icons/Real Estate.png',
    // Core practices
    'corporate': '/icons/Corporate MA.png',
    'litigation': '/icons/Commercial Litigation.png',
    'employment': '/icons/Labor and Employment.png',
    'labor': '/icons/Labor and Employment.png',
    'finance': '/icons/Finance.png',
    'tax': '/icons/Tax.png',
    'healthcare': '/icons/Healthcare Option 1.png',
    'government': '/icons/Government Advocacy.png',
    'energy': '/icons/Energy & Natural Resources.png',
    'construction': '/icons/Construction Option 1.png',
    'technology': '/icons/Technology Option 1.png',
    'data-security': '/icons/Data Security Option 2.png',
    'privacy': '/icons/Data Security Option 2.png',
    'telecommunications': '/icons/Telecommunications.png',
    'infrastructure': '/icons/Infrastructure.png',
    'transportation': '/icons/Transportation Plane.png',
    'private-wealth': '/icons/Private Wealth.png',
    'national-security': '/icons/National Security & Defense.png',
    'hospitality': '/icons/Hospitality.png',
  };
  
  const getPracticeIcon = (practiceId: string) => {
    // first, check S3 icon config
    const iconName = iconConfig?.icons?.[practiceId];
    if (iconName) {
      const ResolvedIcon = resolvePracticeIcon(iconName);
      if (ResolvedIcon) return <ResolvedIcon size={20} />;
      console.warn(`Practice icon "${iconName}" not in static resolver, falling back`);
    }
    // second, check HK PNG icons
    const iconPath = practiceIconMap[practiceId];
    if (iconPath) {
      return <HKIcon src={iconPath} alt={practiceId} />;
    }
    // final fallback
    return <PiBriefcase size={20} />;
  };

  const handleToggleCollapse = () => {
    if (isTemporary) {
      closeDrawer();
    } else {
      onCollapse();
    }
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    if (isTemporary) {
      closeDrawer();
    }
  };

  const togglePractice = (practiceId: string) => {
    if (collapsed) {
      // If collapsed, expand first
      onCollapse();
    }
    setExpandedPractices(prev => 
      prev.includes(practiceId) 
        ? prev.filter(id => id !== practiceId)
        : [...prev, practiceId]
    );
  };

  const toggleForms = () => {
    if (collapsed) {
      onCollapse();
    }
    setExpandedForms(prev => !prev);
  };

  const isActive = (path: string) => location.pathname === path;

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      clearSearch();
    }
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user) return '?';
    const first = user.given_name?.charAt(0) || '';
    const last = user.family_name?.charAt(0) || '';
    return (first + last).toUpperCase() || user.email?.charAt(0).toUpperCase() || '?';
  };

  const drawerContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Sidebar Header with Logo */}
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: collapsed ? 'center' : 'space-between',
          p: 1.5,
          px: collapsed ? 1 : 2,
          borderBottom: `1px solid ${borderColor}`,
          minHeight: 64,
          flexShrink: 0,
        }}
      >
        {!collapsed && (
          <Box 
            component={Link} 
            to="/" 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              textDecoration: 'none',
              transition: 'opacity 0.2s ease',
              '&:hover': { opacity: 0.8 }
            }}
          >
            <Box
              component="img"
              src="/logo-white-transparent.svg"
              alt="HK Workflows"
              sx={{ 
                height: 36,
                maxWidth: 180,
                // In dark mode: keep white. In light mode: tint to HK blue (#012169)
                filter: theme.palette.mode === 'dark' 
                  ? 'none' 
                  : 'brightness(0) saturate(100%) invert(10%) sepia(100%) saturate(5000%) hue-rotate(220deg) brightness(50%)',
              }}
            />
          </Box>
        )}
        <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
          <IconButton 
            onClick={handleToggleCollapse}
            sx={{
              width: 36,
              height: 36,
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: hoverBg,
                color: 'primary.main',
              },
            }}
          >
            {collapsed ? <PiCaretRight size={20} /> : <PiCaretLeft size={20} />}
          </IconButton>
        </Tooltip>
      </Box>
      
      {/* Search Bar - Hidden when collapsed */}
      {!collapsed && (
        <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${borderColor}`, flexShrink: 0 }}>
          <TextField
            inputRef={searchInputRef}
            size="small"
            fullWidth
            placeholder="Search workflows..."
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontSize: 13,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PiMagnifyingGlass size={16} style={{ color: 'inherit' }} />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => { clearSearch(); searchInputRef.current?.focus(); }}
                    edge="end"
                  >
                    <PiX size={16} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      )}

      {/* Navigation Content - Scrollable */}
      <Box sx={{ flex: 1, overflow: 'auto', py: 1 }}>
        <List disablePadding>
          {/* Dashboard */}
          <ListItem disablePadding>
            <Tooltip title={collapsed ? 'Dashboard' : ''} placement="right">
              <ListItemButton
                sx={baseButtonSx}
                onClick={() => handleNavigate('/')}
                selected={isActive('/')}
              >
                <ListItemIcon sx={listItemIconSx}>
                  <PiSquaresFour size={20} />
                </ListItemIcon>
                {!collapsed && <ListItemText primary="Dashboard" />}
              </ListItemButton>
            </Tooltip>
          </ListItem>

          {/* AI Chat */}
          {aiChatEnabled && (
            <ListItem disablePadding>
              <Tooltip title={collapsed ? 'AI Chat' : ''} placement="right">
                <ListItemButton
                  sx={baseButtonSx}
                  onClick={() => handleNavigate('/chat')}
                  selected={isActive('/chat')}
                >
                  <ListItemIcon sx={listItemIconSx}>
                    <PiChatCenteredDots size={20} />
                  </ListItemIcon>
                  {!collapsed && (
                    <>
                      <ListItemText primary="AI Chat" />
                      <Box
                        sx={{
                          px: 1,
                          py: 0.25,
                          fontSize: 11,
                          fontWeight: 600,
                          bgcolor: 'secondary.main',
                          color: 'white',
                          borderRadius: 100,
                        }}
                      >
                        New
                      </Box>
                    </>
                  )}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          )}

          {/* Agents */}
          {aiChatEnabled && (
            <ListItem disablePadding>
              <Tooltip title={collapsed ? 'Agents' : ''} placement="right">
                <ListItemButton
                  sx={baseButtonSx}
                  onClick={() => handleNavigate('/agents')}
                  selected={isActive('/agents')}
                >
                  <ListItemIcon sx={listItemIconSx}>
                    <PiRobot size={20} />
                  </ListItemIcon>
                  {!collapsed && <ListItemText primary="Agents" />}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          )}

          {/* Section Title - Practice Areas */}
          {!collapsed && (
            <Typography
              variant="overline"
              sx={sectionHeaderSx}
            >
              Practice Areas
            </Typography>
          )}

          {collapsed && <Divider sx={{ my: 1, mx: 1 }} />}

          {/* Search results indicator */}
          {!collapsed && searchTerm.trim() && (
            <Box sx={{ px: 2, py: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {filteredPractices.length === 0 && filteredForms.length === 0
                  ? 'No workflows found'
                  : `Showing results for "${searchTerm}"`}
              </Typography>
            </Box>
          )}

          {/* Custom Forms */}
          {customFormsEnabled && filteredForms.length > 0 && (
            <>
              <ListItem disablePadding>
                <Tooltip title={collapsed ? 'Custom Forms' : ''} placement="right">
                  <ListItemButton sx={baseButtonSx} onClick={toggleForms}>
                    <ListItemIcon sx={listItemIconSx}>
                      <PiFile size={20} />
                    </ListItemIcon>
                    {!collapsed && (
                      <>
                        <ListItemText primary="Custom Forms" />
                        <PiCaretDown 
                          size={16} 
                          style={{ 
                            transition: 'transform 0.2s ease',
                            transform: expandedForms ? 'rotate(180deg)' : 'rotate(0deg)'
                          }} 
                        />
                      </>
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>

              {!collapsed && (
                <Collapse in={expandedForms} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {filteredForms.map((form: any) => (
                      <ListItem key={form.id} disablePadding>
                        <ListItemButton
                          sx={nestedLevelOneSx}
                          onClick={() => handleNavigate(`/form/${form.id}`)}
                          selected={isActive(`/form/${form.id}`)}
                        >
                          <ListItemText
                            primary={form.title}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              )}
            </>
          )}

          {/* Practices */}
          {filteredPractices.map((practice: any) => (
            <React.Fragment key={practice.id}>
              <ListItem disablePadding>
                <Tooltip title={collapsed ? formatTitle(practice.name) : ''} placement="right">
                  <ListItemButton sx={baseButtonSx} onClick={() => togglePractice(practice.id)}>
                    <ListItemIcon sx={listItemIconSx}>
                      {getPracticeIcon(practice.id)}
                    </ListItemIcon>
                    {!collapsed && (
                      <>
                        <ListItemText primary={formatTitle(practice.name)} />
                        {expandedPractices.includes(practice.id) 
                          ? <PiCaretDown 
                              size={16} 
                              style={{ 
                                transition: 'transform 0.2s ease',
                                transform: 'rotate(180deg)'
                              }} 
                            />
                          : <PiCaretDown 
                              size={16} 
                              style={{ 
                                transition: 'transform 0.2s ease',
                                transform: 'rotate(0deg)'
                              }} 
                            />
                        }
                      </>
                    )}
                  </ListItemButton>
                </Tooltip>
              </ListItem>

              {!collapsed && (
                <Collapse in={expandedPractices.includes(practice.id)} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {!searchTerm.trim() && (
                      <ListItem disablePadding>
                        <ListItemButton
                          sx={nestedLevelOneSx}
                          onClick={() => handleNavigate(`/${practice.id}`)}
                          selected={isActive(`/${practice.id}`)}
                        >
                          <ListItemText primary="Overview" />
                        </ListItemButton>
                      </ListItem>
                    )}

                    {practice.workflows?.map((workflow: any) => (
                      <ListItem key={workflow.id} disablePadding>
                        <ListItemButton
                          sx={nestedLevelTwoSx}
                          onClick={() => handleNavigate(`/${practice.id}/${workflow.id}`)}
                          selected={isActive(`/${practice.id}/${workflow.id}`)}
                        >
                          <ListItemText
                            primary={workflow.ui?.displayName || formatTitle(workflow.name)}
                            primaryTypographyProps={{ variant: 'body2' }}
                          />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          ))}

          {/* Admin Section */}
          {(isAdmin() || canAccessWorkflowStudio()) && !collapsed && (
            <Typography
              variant="overline"
              sx={sectionHeaderSx}
            >
              Admin
            </Typography>
          )}

          {(isAdmin() || canAccessWorkflowStudio()) && collapsed && (
            <Divider sx={{ my: 1, mx: 1 }} />
          )}

          {/* Session */}
          {isAdmin() && (
            <ListItem disablePadding>
              <Tooltip title={collapsed ? 'Session' : ''} placement="right">
                <ListItemButton
                  sx={baseButtonSx}
                  onClick={() => handleNavigate('/session')}
                  selected={isActive('/session')}
                >
                  <ListItemIcon sx={listItemIconSx}>
                    <PiShield size={20} />
                  </ListItemIcon>
                  {!collapsed && <ListItemText primary="Session" />}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          )}

          {/* Workflow Studio */}
          {canAccessWorkflowStudio() && (
            <ListItem disablePadding>
              <Tooltip title={collapsed ? 'Workflow Studio' : ''} placement="right">
                <ListItemButton
                  sx={baseButtonSx}
                  onClick={() => handleNavigate('/studio')}
                  selected={location.pathname.startsWith('/studio')}
                >
                  <ListItemIcon sx={listItemIconSx}>
                    <PiSliders size={20} />
                  </ListItemIcon>
                  {!collapsed && <ListItemText primary="Workflow Studio" />}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          )}

          {/* Admin */}
          {isAdmin() && (
            <ListItem disablePadding>
              <Tooltip title={collapsed ? 'Admin' : ''} placement="right">
                <ListItemButton
                  sx={baseButtonSx}
                  onClick={() => handleNavigate('/admin')}
                  selected={isActive('/admin')}
                >
                  <ListItemIcon sx={listItemIconSx}>
                    <PiGear size={20} />
                  </ListItemIcon>
                  {!collapsed && <ListItemText primary="Admin" />}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          )}

          {/* Form Builder */}
          {formBuilderEnabled && isAdmin() && (
            <ListItem disablePadding>
              <Tooltip title={collapsed ? 'Form Builder' : ''} placement="right">
                <ListItemButton
                  sx={baseButtonSx}
                  onClick={() => handleNavigate('/forms')}
                  selected={isActive('/forms')}
                >
                  <ListItemIcon sx={listItemIconSx}>
                    <PiFile size={20} />
                  </ListItemIcon>
                  {!collapsed && <ListItemText primary="Form Builder" />}
                </ListItemButton>
              </Tooltip>
            </ListItem>
          )}
        </List>
      </Box>

      {/* Sidebar Footer with Popup Menu */}
      <Box 
        sx={{ 
          borderTop: `1px solid ${borderColor}`,
          flexShrink: 0,
          p: collapsed ? 1 : 1.5,
        }}
      >
        {/* User Button - Opens Menu */}
        {user && (
          <Box
            component="button"
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => setMenuAnchorEl(e.currentTarget)}
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              p: collapsed ? 1 : 1.5,
              borderRadius: 1.5,
              border: 'none',
              backgroundColor: 'transparent',
              cursor: 'pointer',
              justifyContent: collapsed ? 'center' : 'flex-start',
              transition: 'all 0.15s ease',
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: hoverBg,
                color: 'primary.main',
              },
            }}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: 'primary.main',
                fontSize: 13,
                fontWeight: 600,
              }}
            >
              {getUserInitials()}
            </Avatar>
            {!collapsed && (
              <>
                <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      color: 'inherit',
                    }}
                  >
                    {user.given_name} {user.family_name}
                  </Typography>
                </Box>
                <PiCaretUp 
                  size={16}
                  style={{ 
                    transition: 'transform 0.2s',
                    transform: menuAnchorEl ? 'rotate(0deg)' : 'rotate(180deg)',
                  }}
                />
              </>
            )}
          </Box>
        )}

        {/* Popup Menu */}
        <Popover
          open={Boolean(menuAnchorEl)}
          anchorEl={menuAnchorEl}
          onClose={() => setMenuAnchorEl(null)}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          slotProps={{
            paper: {
              sx: {
                width: collapsed ? 240 : DRAWER_WIDTH - 24,
                mb: 1,
                borderRadius: 2,
                border: `1px solid ${borderColor}`,
                boxShadow: theme.shadows[8],
              }
            }
          }}
        >
          <Box sx={{ py: 1 }}>
            {/* Theme Toggle */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2,
                py: 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, color: 'text.secondary' }}>
                {themeMode === 'dark' ? <IoMoonSharp size={16} /> : <IoSunnyOutline size={16} />}
                <Typography variant="body2">Dark Mode</Typography>
              </Box>
              <Switch
                size="small"
                checked={themeMode === 'dark'}
                onChange={(e) => onToggleTheme(false)}
              />
            </Box>

            <Divider sx={{ my: 1 }} />

            {/* Contact Us */}
            <Box
              component="a"
              href="mailto:innovation@hklaw.com"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1,
                color: 'text.secondary',
                textDecoration: 'none',
                transition: 'all 0.15s ease',
                '&:hover': {
                  backgroundColor: hoverBg,
                  color: 'primary.main',
                },
              }}
            >
              <PiEnvelope size={16} />
              <Typography variant="body2">Contact Innovation</Typography>
            </Box>

            <Divider sx={{ my: 1 }} />

            {/* Sign Out */}
            <Box
              component="button"
              onClick={() => {
                setMenuAnchorEl(null);
                signOut();
              }}
              sx={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1,
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                color: 'text.secondary',
                textAlign: 'left',
                transition: 'all 0.15s ease',
                '&:hover': {
                  backgroundColor: hoverBg,
                  color: 'primary.main',
                },
              }}
            >
              <PiSignOut size={16} />
              <Typography variant="body2">Sign Out</Typography>
            </Box>
          </Box>
        </Popover>
      </Box>
    </Box>
  );

  if (isTemporary) {
    return (
      <SwipeableDrawer
        anchor="left"
        open={open}
        onOpen={handleSwipeOpen}
        onClose={handleSwipeClose}
        disableSwipeToOpen={!touchFriendly}
        ModalProps={{ keepMounted: true }}
        PaperProps={{ sx: drawerPaperSx }}
      >
        {drawerContent}
      </SwipeableDrawer>
    );
  }

  return (
    <Drawer
      variant={variant}
      anchor="left"
      open={open}
      onClose={onClose ?? onToggle}
      sx={{
        width: open ? (collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH) : 0,
        flexShrink: 0,
        transition: 'width 0.3s ease',
      }}
      ModalProps={{ keepMounted: true }}
      PaperProps={{ sx: drawerPaperSx }}
    >
      {drawerContent}
    </Drawer>
  );
}
